/**
 * Editor for clause options within the ClauseForm.
 * Handles adding/removing options (2-4) with ID, label, and description
 * fields. Displays computed Word template tags per option
 * (e.g. clause_{clauseId}_{optionId}).
 * Uses brand-compliant .input class on text inputs/selects, .label class on <label> elements.
 *
 * An "In use" chip renders beside any option whose id appears in the
 * optional `referencedOptionIds` prop (option ids the active org template's
 * tags reference) — advisory only, never disables removal. Absent prop
 * (create mode, global templates) renders no chips.
 *
 * Preview wording (option.text) is read-only here: it is derived from the
 * uploaded template document on version activation, not authored in the
 * admin UI. It renders via the shared DocumentStylePreview so admins see the
 * same document-style rendering (headings, drafting notes, tables) users see
 * in the wizard, rather than raw marker text.
 */

import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../common';
import DocumentStylePreview from '../common/DocumentStylePreview';
import TagPreview from './TagPreview';
import { clauseTagKey, checkTagKey, unansweredTagKey } from '../../utils/clauseTagParser';
import type { ClauseOption } from '../../types';

interface ClauseOptionsEditorProps {
  options: ClauseOption[];
  onChange: (options: ClauseOption[]) => void;
  defaultOptionId: string;
  onDefaultChange: (optionId: string) => void;
  clauseId: string;
  errors?: {
    options?: string;
    optionLabels?: string;
    optionIds?: string;
  };
  /** Option ids the active org template's tags reference — renders an
   *  "In use" chip beside each matching option. Omitted for create mode
   *  and global templates (no chips). */
  referencedOptionIds?: string[];
}

let optionCounter = 100;
const emptyOption = (): ClauseOption => ({
  id: `opt-${++optionCounter}`,
  label: '',
  description: '',
});

export default function ClauseOptionsEditor({
  options,
  onChange,
  defaultOptionId,
  onDefaultChange,
  clauseId,
  errors,
  referencedOptionIds,
}: ClauseOptionsEditorProps) {
  const handleOptionChange = (index: number, field: keyof ClauseOption, value: string) => {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    onChange(newOptions);
  };

  const addOption = () => {
    if (options.length < 4) {
      onChange([...options, emptyOption()]);
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      const removedId = options[index].id;
      const newOptions = options.filter((_, i) => i !== index);
      onChange(newOptions);
      if (defaultOptionId === removedId) {
        onDefaultChange('');
      }
    }
  };

  const errorMessage = errors?.options || errors?.optionLabels || errors?.optionIds;

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label">
            Options <span className="text-red-500">*</span>
          </label>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addOption}
            disabled={options.length >= 4}
            icon={<Plus className="h-4 w-4" />}
          >
            Add Option
          </Button>
        </div>

        {errorMessage && <p className="mb-2 text-body-sm text-red-600 dark:text-red-400">{errorMessage}</p>}

        <div className="space-y-3">
          {options.map((option, index) => (
            <div
              key={index}
              data-testid={`clause-option-row-${option.id}`}
              className="border dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-700"
            >

              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-2">
                  {referencedOptionIds?.includes(option.id) && (
                    <span className="badge-draft">In use</span>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={option.id}
                      onChange={(e) => handleOptionChange(index, 'id', e.target.value)}
                      placeholder="Option ID"
                      className="input"
                    />
                    <input
                      type="text"
                      value={option.label}
                      onChange={(e) => handleOptionChange(index, 'label', e.target.value)}
                      placeholder="Option label"
                      className="input"
                    />
                  </div>
                  <input
                    type="text"
                    value={option.description || ''}
                    onChange={(e) => handleOptionChange(index, 'description', e.target.value)}
                    placeholder="Option description (optional)"
                    className="input"
                  />
                  <div role="group" aria-labelledby={`option-preview-label-${index}`}>
                    <span id={`option-preview-label-${index}`} className="label">
                      Preview wording
                    </span>
                    {option.text ? (
                      <div className="mt-1 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3">
                        <DocumentStylePreview text={option.text} bare />
                      </div>
                    ) : (
                      <p className="mt-1 text-body-sm italic text-slate-400 dark:text-slate-500">
                        No wording in template document.
                      </p>
                    )}
                  </div>
                  {clauseId && option.id && (
                    <div
                      role="group"
                      aria-label={`Template tags for option ${option.id}`}
                      className="space-y-1 border-t border-slate-200 dark:border-slate-600 pt-2"
                    >
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-caption text-slate-500 dark:text-slate-400 w-20 shrink-0">
                          Wording
                        </span>
                        <TagPreview
                          tag={clauseTagKey(clauseId, option.id)}
                          label={`wording for option ${option.id}`}
                        />
                      </div>
                      <div className="flex items-start gap-2 flex-wrap">
                        <span className="text-caption text-slate-500 dark:text-slate-400 w-20 shrink-0">
                          Checkbox
                        </span>
                        <TagPreview
                          tag={checkTagKey(clauseId, option.id)}
                          variant="plain"
                          label={`checkbox marker for option ${option.id}`}
                        />
                      </div>
                    </div>
                  )}
                </div>
                <Button variant="danger" iconOnly size="sm" icon={<Trash2 className="h-4 w-4" />} disabled={options.length <= 2} aria-label="Remove option" className="btn-icon-compact" type="button" onClick={() => removeOption(index)} />
              </div>
            </div>
          ))}
        </div>

        {clauseId && (
          <div className="mt-3 flex items-start gap-2 flex-wrap">
            <span className="text-caption text-slate-500 dark:text-slate-400 shrink-0">
              Clause guidance (hidden once answered)
            </span>
            <TagPreview
              tag={unansweredTagKey(clauseId)}
              label={`guidance block for clause ${clauseId}`}
            />
          </div>
        )}
      </div>

      {/* Default Option */}
      <div>
        <label htmlFor="clause-default" className="label">
          Default Option
        </label>
        <select
          id="clause-default"
          value={defaultOptionId}
          onChange={(e) => onDefaultChange(e.target.value)}
          className="input mt-1"
        >
          <option value="">No default</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label || option.id}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
